import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Col, Row } from "reactstrap";
import { PathwayCard, PathwayCardBody, PathwayCardHeader } from "../../_UI/Components/PathwayCard";
import ConfirmDeletionModalWithText from "../../_components/ConfirmDeletionModalWithText";
import PathwayPagination from "../../_UI/Components/PathwayPagination";
import {
    LoadingSpinnerButton,
    LoadingSpinnerDropdownButton,
    PathwayButton,
    PathwayDeleteButton,
} from "../../_UI/Components/PathwayButtons";
import styled from "styled-components";
import LoadingSpinner from "../../_UI/Components/LoadingSpinner";
import PathwayTextInputGroup from "../../_UI/Components/Inputs/PathwayTextInputGroup";
import { adminAreaActions } from "../services/store/adminarea.actions";
import { clearHubspotSession } from "../../user/services/store/authentication.actions";
import { selectOrganization } from "../../user/services/store/user.actions";
import PathwaySelectInputGroup from "../../_UI/Components/Inputs/PathwaySelectInputGroup";
import { TableContentDividerLine } from "../../_UI/StyledComponents/PathwayTable";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { useNavigate } from "react-router";
import { InfoIcon, LoginIcon, PathwayIconWrapperButton } from "../../_UI/StyledComponents/PathwayIcons";
import { OrganizationType } from "../../_statics/OrganizationType";
import { UserType } from "../../_statics/UserStatics";
import stringUtils from "../../_services/string-utils";

// E2E-only test account used in automated productfruits tests.
export const E2E_TAX_CONSULTANT_EMAIL = "e2e@pathway-solutions.de";

const OrganizationContent = () => {
    const dispatch = useDispatch();
    const adminArea = useSelector((state) => state.adminArea);
    const authentication = useSelector((state) => state.authentication);
    const switchingOrganization = useSelector((state) => state.user.switchOrganizationLoading);
    const navigate = useNavigate();

    // TODO: Move to reducer
    const [deletionModal, setDeletionModal] = useState(false);
    const [organizationToDelete, setOrganizationToDelete] = useState();

    const { isSuperUser, selectedOrganization } = useUserAuthentication();

    const clearDeletionErrorMessages = useCallback(() => {
        dispatch(adminAreaActions.clearDeletionErrorMessages());
    }, [dispatch]);

    const setFilterProperties = useCallback(
        (filterProperties) => {
            dispatch(adminAreaActions.setFilterProperties(filterProperties));
        },
        [dispatch]
    );

    const fetchOrganizations = useCallback(
        (filterProperties) => {
            dispatch(adminAreaActions.getAllOrganizations(filterProperties));
        },
        [dispatch]
    );

    const addOrganization = useCallback(() => {
        navigate("/adminarea/organization");
    }, [navigate]);

    const toggleDeletionModal = useCallback(
        (organization) => {
            clearDeletionErrorMessages();
            setDeletionModal(!!organization);
            setOrganizationToDelete(organization);
        },
        [clearDeletionErrorMessages, setDeletionModal, setOrganizationToDelete]
    );

    const handleFilterChange = useCallback(
        (e) => {
            const { name, value } = e.target;
            let tempFilterProperties = adminArea.filterProperties
                ? {
                      name: adminArea.filterProperties.name,
                      module: adminArea.filterProperties["module"],
                      type: adminArea.filterProperties.type,
                      accountName: adminArea.filterProperties.accountName,
                      shop: adminArea.filterProperties.shop,
                      clientNumber: adminArea.filterProperties.clientNumber,
                      consultantNumber: adminArea.filterProperties.consultantNumber,
                      page: adminArea.filterProperties.currentPage,
                      currentPage: adminArea.filterProperties.currentPage,
                      [name]: value,
                  }
                : {
                      name: null,
                      module: null,
                      type: null,
                      accountName: null,
                      shop: null,
                      clientNumber: null,
                      consultantNumber: null,
                      page: 0,
                      [name]: value,
                  };

            setFilterProperties(tempFilterProperties);
        },
        [adminArea.filterProperties, setFilterProperties]
    );

    const onSearchSubmit = useCallback(() => {
        let filter = adminArea.filterProperties;
        if (filter.name === "") {
            filter.name = null;
        }
        if (filter.module === "") {
            filter.module = null;
        }
        if (filter.type === "") {
            filter.type = null;
        }

        filter.page = 0;
        fetchOrganizations(filter);
    }, [adminArea.filterProperties, fetchOrganizations]);

    const triggerHubspot = useCallback(() => {
        window.location.href =
            "https://app-eu1.hubspot.com/oauth/authorize?client_id=" +
            process.env.REACT_APP_HUBSPOT_CLIENT_ID +
            "&redirect_uri=" +
            process.env.REACT_APP_HUBSPOT_REDIRECT_URL +
            "&scope=" +
            process.env.REACT_APP_HUBSPOT_SCOPE;
    }, []);

    const clearHubspot = useCallback(() => {
        dispatch(clearHubspotSession());
    }, [dispatch]);

    const hubspotDropdownEntries = useMemo(() => {
        return !authentication.hubspotSessionExists
            ? [[<strong>Connect to Hubspot</strong>, triggerHubspot, null]]
            : [
                  [`Connected App: ${authentication.hubspotSessionExists.hubspotApp}`, null, null],
                  [`Connected App ID: ${authentication.hubspotSessionExists.hubspotAppId}`, null, null],
                  [`Environment: ${authentication.hubspotSessionExists.environment}`, null, null],
                  [<strong>Log out of Hubspot</strong>, clearHubspot, null],
              ];
    }, [authentication.hubspotSessionExists, triggerHubspot, clearHubspot]);

    const switchToOrganization = useCallback(
        (organizationUUID) => {
            if (switchingOrganization) {
                return;
            }
            dispatch(selectOrganization(organizationUUID, true));
        },
        [dispatch, switchingOrganization]
    );

    const deleteOrganization = useCallback(
        (organizationUUID) => {
            dispatch(adminAreaActions.deleteOrganization(organizationUUID)).then((result) => {
                if (result && result.externalUUID) {
                    toggleDeletionModal(null);
                }
            });
        },
        [dispatch, toggleDeletionModal]
    );

    return (
        <>
            <ConfirmDeletionModalWithText
                show={!!deletionModal}
                content={"Soll diese Organisation wirklich gelöscht werden?"}
                buttonCyData="delete-organization-modal-button"
                confirmDeletion={() => {
                    deleteOrganization(organizationToDelete.externalUUID);
                }}
                toggle={() => setDeletionModal(null)}
                title="Organisation löschen"
                loading={!!adminArea.deletionLoading}
                errorMessage={adminArea.deletionErrorMessage}
            />

            <PathwayCard>
                <PathwayCardHeader>
                    <h3>Organisationen</h3>

                    <ButtonContainer>
                        {isSuperUser && (
                            <LoadingSpinnerDropdownButton
                                loading={!!authentication.hubspotSessionClearing}
                                color={authentication.hubspotSessionExists ? "success" : "danger"}
                                items={hubspotDropdownEntries}
                            >
                                {authentication.hubspotSessionExists ? "Hubspot Connected" : "Hubspot Not Connected"}{" "}
                            </LoadingSpinnerDropdownButton>
                        )}

                        <PathwayButton onClick={addOrganization} dataCy="add-organization-button">
                            Neue Organisation
                        </PathwayButton>
                    </ButtonContainer>
                </PathwayCardHeader>
                <PathwayCardBody style={{ paddingBottom: "1em" }}>
                    <SearchBarLayout>
                        <PathwayTextInputGroup
                            name="name"
                            label="Name"
                            handleChange={handleFilterChange}
                            value={
                                adminArea.filterProperties && adminArea.filterProperties.name
                                    ? adminArea.filterProperties.name
                                    : ""
                            }
                            disabled={adminArea.organizationsLoading}
                        />

                        <PathwayTextInputGroup
                            name="accountName"
                            label="Accounts"
                            handleChange={handleFilterChange}
                            value={
                                adminArea.filterProperties && adminArea.filterProperties.accountName
                                    ? adminArea.filterProperties.accountName
                                    : ""
                            }
                            disabled={adminArea.organizationsLoading}
                        />

                        <PathwayTextInputGroup
                            name="shop"
                            label="Shops/APIs"
                            handleChange={handleFilterChange}
                            value={
                                adminArea.filterProperties && adminArea.filterProperties.shop
                                    ? adminArea.filterProperties.shop
                                    : ""
                            }
                            disabled={adminArea.organizationsLoading}
                        />

                        <PathwayTextInputGroup
                            name="consultantNumber"
                            label="Beraternummer"
                            handleChange={handleFilterChange}
                            value={
                                adminArea.filterProperties && adminArea.filterProperties.consultantNumber
                                    ? adminArea.filterProperties.consultantNumber
                                    : ""
                            }
                            disabled={adminArea.organizationsLoading}
                        />

                        <PathwayTextInputGroup
                            name="clientNumber"
                            label="Mandantennummer"
                            handleChange={handleFilterChange}
                            value={
                                adminArea.filterProperties && adminArea.filterProperties.clientNumber
                                    ? adminArea.filterProperties.clientNumber
                                    : ""
                            }
                            disabled={adminArea.organizationsLoading}
                        />

                        {isSuperUser ? (
                            <PathwaySelectInputGroup
                                name="type"
                                label="Typ"
                                handleChange={handleFilterChange}
                                value={
                                    adminArea.filterProperties && adminArea.filterProperties.type
                                        ? adminArea.filterProperties.type
                                        : ""
                                }
                                disabled={adminArea.organizationsLoading}
                                nullOptionValue="-"
                            >
                                <option value="STANDARD">Standart</option>
                                <option value="LEX_OFFICE">LexOffice</option>
                                <option value="SHOPIFY_APPSTORE">AppStore</option>
                            </PathwaySelectInputGroup>
                        ) : (
                            <div />
                        )}

                        {isSuperUser ? (
                            <PathwaySelectInputGroup
                                label="Module"
                                name="module"
                                handleChange={handleFilterChange}
                                value={
                                    adminArea.filterProperties && adminArea.filterProperties.module
                                        ? adminArea.filterProperties.module
                                        : ""
                                }
                                disabled={adminArea.organizationsLoading}
                                nullOptionValue="-"
                            >
                                {adminArea.allApplications &&
                                    adminArea.allApplications.map((application, index) => {
                                        return (
                                            <option key={index} value={application.name}>
                                                {application.name}
                                            </option>
                                        );
                                    })}
                            </PathwaySelectInputGroup>
                        ) : (
                            <div />
                        )}

                        <LoadingSpinnerButton
                            name="searchButton"
                            buttonCyData="search-organization-content-button"
                            loading={!!adminArea.organizationsLoading}
                            onClick={onSearchSubmit}
                        >
                            Suchen
                        </LoadingSpinnerButton>
                    </SearchBarLayout>

                    <br />
                    <TableContentDividerLine />

                    {adminArea.organizationsLoading ? (
                        <Row>
                            <Col>
                                <br />
                                <LoadingSpinner loading={!!adminArea.organizationsLoading} size="4rem" />
                                <br />
                            </Col>
                        </Row>
                    ) : adminArea.organizations ? (
                        adminArea.organizations.length > 0 ? (
                            <Row>
                                <Col>
                                    <div className="table-responsive"><table className="table">
                                        <thead>
                                            <tr>
                                                <th>Agentur</th>
                                                <th>Zahlungsmethode</th>
                                                <th>BillingAgreement</th>
                                                <th>Name</th>
                                                <th>Ansprechpartner</th>
                                                <th>Accounts</th>
                                                <th>Shops/APIs</th>
                                                <th>Beraternummer/Mandantennummer</th>
                                                {!adminArea.organizationsLoading &&
                                                    adminArea.organizations &&
                                                    adminArea.organizations.length >= 0 && (
                                                        <th>
                                                            {adminArea.totalElements &&
                                                                "Anzahl: " + adminArea.totalElements}
                                                        </th>
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {adminArea.organizations &&
                                                adminArea.organizations.map((organization, index) => (
                                                    <tr key={index}>
                                                        <>
                                                            {organization.organizationType ===
                                                            OrganizationType.TAX_AGENCY ? (
                                                                <td>
                                                                    <i className="fas fa-check"></i>
                                                                </td>
                                                            ) : (
                                                                <td>
                                                                    <i className="fas fa-close"></i>
                                                                </td>
                                                            )}
                                                        </>
                                                        <td>
                                                            {organization.blockedIfNoPaymentMethod ? (
                                                                <i
                                                                    style={{
                                                                        fontSize: "1.4em",
                                                                        color: organization.hasValidBillingInformation
                                                                            ? "green"
                                                                            : "red",
                                                                    }}
                                                                    className={"fas fa-lock"}
                                                                ></i>
                                                            ) : (
                                                                <i
                                                                    style={{
                                                                        fontSize: "1.4em",
                                                                        color: organization.hasValidBillingInformation
                                                                            ? "green"
                                                                            : "red",
                                                                    }}
                                                                    className={"fas fa-lock-open"}
                                                                ></i>
                                                            )}
                                                        </td>
                                                        <td>{organization.billingAgreementType || ""}</td>
                                                        <td className="">{organization.name}</td>
                                                        <td className="">
                                                            {organization.contactMail &&
                                                            organization.contactMail !== "" &&
                                                            organization.contact &&
                                                            organization.contact !== "" ? (
                                                                <a href={"mailto:" + organization.contactMail}>
                                                                    {organization.contact}
                                                                </a>
                                                            ) : (
                                                                <>{organization.contact || ""}</>
                                                            )}
                                                        </td>
                                                        <td style={{ minWidth: "200px" }}>
                                                            <ul className="pl-0">
                                                                {organization?.clients?.map(
                                                                    (cl) => cl.name && <li>{cl.name}</li>
                                                                )}
                                                            </ul>
                                                        </td>
                                                        <td style={{ minWidth: "200px" }}>
                                                            <ul className="pl-0">
                                                                {organization?.shopEndpoints?.map(
                                                                    (shop) => shop && <li>{shop}</li>
                                                                )}
                                                            </ul>
                                                        </td>
                                                        <td>
                                                            {organization?.clients
                                                                ?.map(
                                                                    (cl) =>
                                                                        (cl.consultantNumber || cl.clientNumber) &&
                                                                        `${cl.consultantNumber || ""}-${cl.clientNumber || ""}`
                                                                )
                                                                .filter(Boolean)
                                                                .join(", ")}
                                                        </td>
                                                        <td style={{ minWidth: "130px", width: "130px" }}>
                                                            <ActionCell>
                                                                {isSuperUser && (
                                                                    <PathwayIconWrapperButton
                                                                        title="In Organisation wechseln"
                                                                        data-cy="switch-organization-button"
                                                                        disabled={
                                                                            !!switchingOrganization ||
                                                                            organization.externalUUID ===
                                                                                selectedOrganization?.externalUUID
                                                                        }
                                                                        onClick={() =>
                                                                            switchToOrganization(organization.externalUUID)
                                                                        }
                                                                    >
                                                                        <LoginIcon />
                                                                    </PathwayIconWrapperButton>
                                                                )}
                                                                <PathwayIconWrapperButton
                                                                    title="Organisation bearbeiten"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            "/adminarea/organization/" +
                                                                                organization.externalUUID
                                                                        )
                                                                    }
                                                                >
                                                                    <InfoIcon />
                                                                </PathwayIconWrapperButton>
                                                                {(isSuperUser ||
                                                                    (authentication.user &&
                                                                        authentication.user.platformUserType ===
                                                                            UserType.TAXCONSULTANT &&
                                                                        stringUtils.equals(
                                                                            E2E_TAX_CONSULTANT_EMAIL,
                                                                            authentication.user.mail
                                                                        ))) && (
                                                                    <PathwayDeleteButton
                                                                        dataCy="delete-organization-button"
                                                                        onClick={() => toggleDeletionModal(organization)}
                                                                    />
                                                                )}
                                                            </ActionCell>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table></div>
                                    {!adminArea.organizationsLoading &&
                                        (!adminArea.organizations || adminArea.organizations.length === 0) && (
                                            <Row>
                                                <Col>
                                                    <h3
                                                        style={{
                                                            marginTop: "2em",
                                                            marginBottom: "2em",
                                                        }}
                                                    >
                                                        Es konnten keine Organisationen gefunden werden
                                                    </h3>
                                                </Col>
                                            </Row>
                                        )}
                                    <Row className="mb-2">
                                        <Col>
                                            <LoadingSpinner loading={adminArea.organizationsLoading} size="4em" />
                                        </Col>
                                    </Row>

                                    <PathwayPagination
                                        currentPage={adminArea.filterProperties.currentPage}
                                        totalPages={adminArea.totalPages}
                                        onPageChange={(page) => fetchOrganizations({ ...adminArea.filterProperties, page })}
                                        show={!!adminArea.filterProperties && !!adminArea.organizations}
                                        loading={!!adminArea.organizationsLoading}
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Row>
                                <Col>
                                    <br />
                                    <h4 style={{ textAlign: "center" }}>
                                        Es konnten keine Organisation gefunden werden
                                    </h4>
                                    <br />
                                </Col>
                            </Row>
                        )
                    ) : (
                        <Row>
                            <Col>
                                <br />
                                <h4 style={{ textAlign: "center" }}>Es wurden noch keine Organisationen geladen</h4>
                                <br />
                            </Col>
                        </Row>
                    )}
                </PathwayCardBody>
            </PathwayCard>
        </>
    );
};

export default OrganizationContent;

const SearchBarLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-auto-rows: minmax(4fr, auto);
    grid-template-rows: 64px 64px;
    column-gap: var(--gap-l);
    align-items: end;
`;

const ActionCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--gap-s);
`;

const ButtonContainer = styled.div`
    display: grid;
    column-gap: var(--gap-l);
    grid-template-columns: auto auto;
`;
